import { isEmpty, keyBy } from 'lodash';

import api from '../lib/api';
import { FieldAttributeType } from '../constants';

export default {
  namespaced: true,

  state: {
    defaultIndicator: '',
    indicatorId: 0,
    displayRev: null,
    indicator: {},
    indicatorInitialized: false,

    filters: [],

    geoGroupsQueue: [], // track geo types which are is being requested
    geoGroups: [],
    geoCodesByGeoGroup: {},
    activeGeoType: null,
    activeGeoCode: undefined,
    activeCompareGeoCode: undefined,
  },

  getters: {
    indicatorId: ({ indicatorId }) => indicatorId,

    indicatorInitialized: ({ indicatorInitialized }) => indicatorInitialized,

    filtersByType: ({ filters }) => keyBy(filters, 'name'),

    indicatorConfig: ({ indicator }) => indicator.config || {},

    defaultIndicator: ({ defaultIndicator }) => defaultIndicator || '',

    breakdowns: ({ indicator }) => indicator.layers || [],

    breakdownsById: (_, { breakdowns }) => keyBy(breakdowns, 'slug'),

    defaultBreakdown: (_, { breakdowns, indicatorConfig, breakdownsById }) =>
      breakdownsById[indicatorConfig.defaultBreakdown] || breakdowns[0],

    displayRev: ({ displayRev }) => displayRev,
    dataVersion: ({ indicator }) => indicator.version,

    geoCodesByGeoGroup: ({ geoCodesByGeoGroup }) => (geoGroup) =>
      geoCodesByGeoGroup[geoGroup],
    geoCodes: ({ geoCodesByGeoGroup }) =>
      Object.keys(geoCodesByGeoGroup).flatMap(
        (geoGroup) => geoCodesByGeoGroup[geoGroup],
      ),

    geoSections: ({ geoGroups, geoCodesByGeoGroup }) => {
      // geo group identificator is geo code (first) associated with geo group
      return geoGroups.map(
        ({ geo_code_long: geoGroupId, geo_group_name: geoGroupName }) => ({
          id: geoGroupId,
          text: geoGroupName,
          children: geoCodesByGeoGroup[geoGroupName]
            ? Object.entries(geoCodesByGeoGroup[geoGroupName]).map(
                ([geoCode, geoCodeLabel]) => ({
                  id: geoCode,
                  text: geoCodeLabel,
                  section: geoGroupId,
                }),
              )
            : [],
        }),
      );
    },

    activeGeoType: ({ activeGeoType }) => activeGeoType,
    activeGeoCode: ({ activeGeoCode }) => activeGeoCode,
    activeCompareGeoCode: ({ activeCompareGeoCode }) => activeCompareGeoCode,
  },

  actions: {
    async fetchIndicatorGeoSelectorOptions(
      { commit, dispatch, getters, state },
      { id, displayRev, geoCode, defaultGeoCode },
    ) {
      const queryGeoCode = geoCode || defaultGeoCode;
      if (queryGeoCode && getters.geoCodes.includes(queryGeoCode)) return;

      if (state.geoGroupsQueue.includes(geoCode)) return;

      // Put requested geo type in queue
      // to eliminate similar subsequent requests.
      commit('geoGroupsQueueUpdated', { item: queryGeoCode });

      const params = {
        displayRev,
        geoCode: queryGeoCode,
      };
      const geos = await api.get(`/display-groups/${id}/geo-selector-options`, {
        params,
      });

      const geoCodes = geos.data?.results ?? [];
      const geoGroups = geos.data?.groups ?? [];
      const queryGeoGroup = geos.data?.group;

      commit('geoDataUpdated', {
        queryGeoGroup,
        geoGroups,
        geoCodes,
      });

      if (!getters.activeGeoCode && defaultGeoCode) {
        dispatch('initGeography', { geoCode: defaultGeoCode });
      }

      commit('geoGroupsQueueUpdated', { item: queryGeoCode, newItem: false });
    },

    async fetchIndicator({ dispatch, commit }, { id, displayRev, geoCode }) {
      const res = await api.fetchIndicator(id, displayRev);

      const { results, attributes, displays } = res;
      const indicator = { ...results, layers: displays };

      if (!results || !results.config) {
        // Simulate 404 when fetching a breakdown id as an indicator (legacy
        // workaround, probably safe to remove)
        const err = new Error('Attempt to load breakdown as indicator');
        err.response = {
          status: 404,
          statusText: 'Not Found',
          data: { message: 'The indicator could not be found' },
        };
        throw err;
      }

      // Extract geoType from geoCode
      const displayWithDefaultGeoCode = (displays || []).find(
        (display) =>
          !isEmpty(display.config) && !isEmpty(display.config.geoCodes),
      );
      // Get default geo code from one from first display config
      const defaultGeoCode =
        displayWithDefaultGeoCode?.config.geoCodes[0] ?? undefined;

      await dispatch('fetchIndicatorGeoSelectorOptions', {
        id,
        displayRev,
        geoCode,
        defaultGeoCode,
      });

      const getLabel = (fieldName) => {
        const result = indicator.layers[0].config.contexts.find(
          (item) => item.field === fieldName,
        );
        if (result && result.label) return result.label;
        return fieldName;
      };

      const filters =
        attributes && attributes.reduce
          ? Object.values(
              attributes
                .filter((attr) => attr.type !== FieldAttributeType.COLOR)
                .reduce((acc, attr) => {
                  acc[attr.field] = acc[attr.field] || {
                    name: attr.field,
                    options: [],
                    label: getLabel(attr.field),
                    defaultValue: '1',
                  };
                  if (attr.key === '*') acc[attr.field].label = attr.value;
                  else
                    acc[attr.field].options.push({
                      name: attr.key,
                      label: attr.value || attr.key,
                    });
                  return acc;
                }, {}),
            )
          : null;

      commit('setFilters', { filters });

      commit('setIndicator', {
        indicator,
        id: results.slug,
        displayRev: results.revision,
      });
    },

    initGeography({ commit }, { geoCode }) {
      const geoType = geoCode ? String(geoCode).substring(0, 2) : null;

      commit('geoCodeUpdated', geoCode);
      commit('geoTypeUpdated', geoType);
    },

    updateGeography({ commit }, { geoType, geoCode }) {
      if (geoType) {
        commit('geoTypeUpdated', geoType.id);
        commit('geoCodeUpdated', undefined);
      }

      if (geoCode) {
        commit('geoCodeUpdated', geoCode.id);
      }
    },
    updateCompareGeoCode({ commit }, geoCode) {
      commit('compareGeoCodeUpdated', geoCode);
    },
    resetIndicator({ commit }) {
      commit('indicatorStateSetToDefault');
    },
  },

  /* eslint-disable no-param-reassign */
  mutations: {
    setIndicator: (state, { indicator, id, displayRev }) => {
      state.indicator = indicator;
      state.indicatorId = id;
      state.displayRev = displayRev;

      state.indicatorInitialized = true;
    },

    indicatorStateSetToDefault: (state) => {
      state.indicator = {};
      state.indicatorId = 0;
      state.displayRev = null;

      state.indicatorInitialized = false;
    },

    setFilters: (state, { filters }) => {
      state.filters = filters;
    },

    geoGroupsQueueUpdated: (state, { item, newItem = true }) => {
      if (newItem) {
        state.geoGroupsQueue = state.geoGroupsQueue.concat([item]);
      } else {
        state.geoGroupsQueue = state.geoGroupsQueue.filter(
          (queuedItem) => queuedItem !== item,
        );
      }
    },

    geoDataUpdated: (state, { queryGeoGroup, geoCodes, geoGroups }) => {
      state.geoCodesByGeoGroup = {
        ...state.geoCodesByGeoGroup,
        [queryGeoGroup]: geoCodes,
      };
      state.geoGroups = geoGroups.slice();
    },

    setDefaultIndicator: (state, payload) => {
      state.defaultIndicator = payload;
    },

    geoTypeUpdated: (state, geoType) => {
      state.activeGeoType = geoType;
    },
    geoCodeUpdated: (state, geoCode) => {
      state.activeGeoCode = geoCode;
    },
    compareGeoCodeUpdated: (state, geoCode) => {
      state.activeCompareGeoCode = geoCode;
    },
  },
  /* eslint-enable no-param-reassign */
};
